/**
 * Homography — perspective transform from 4 source points to 4 destination points.
 *
 * Used to map the exam paper's 4 corners (visible in the webcam at an angle)
 * to a normalized rectangle (0,0)→(1,1). Once the 3×3 matrix H is computed,
 * any point (x,y) in webcam space can be mapped to paper coordinates (u,v).
 *
 * This lets us classify WHERE on the paper the student is looking or writing,
 * even though the paper appears skewed/perspective-distorted in the camera.
 *
 * Math: solve H such that dst = H × src (homogeneous coordinates).
 * 4 point pairs → 8 equations → 8 unknowns (h1..h8, h9=1).
 */

export interface Point2D {
  x: number;
  y: number;
}

/**
 * Paper region classification.
 * Paper is divided into a 3×3 grid + outside area.
 */
export type PaperRegion =
  | 'top_left'    | 'top_center'    | 'top_right'
  | 'mid_left'    | 'mid_center'    | 'mid_right'
  | 'bottom_left' | 'bottom_center' | 'bottom_right'
  | 'outside';

export const PAPER_REGION_LABELS: Record<PaperRegion, string> = {
  top_left:      '시험지 좌상',
  top_center:    '시험지 상단',
  top_right:     '시험지 우상',
  mid_left:      '시험지 좌측',
  mid_center:    '시험지 중앙',
  mid_right:     '시험지 우측',
  bottom_left:   '시험지 좌하',
  bottom_center: '시험지 하단',
  bottom_right:  '시험지 우하',
  outside:       '시험지 밖',
};

/**
 * Compute a 3×3 homography matrix that maps `src` points to `dst` points.
 *
 * @param src 4 source points (e.g. paper corners in webcam image, normalized 0-1)
 * @param dst 4 destination points (e.g. corners of a unit rectangle)
 * @returns 3×3 matrix as a flat Float64Array[9], row-major. null if degenerate.
 */
export function computeHomography(
  src: [Point2D, Point2D, Point2D, Point2D],
  dst: [Point2D, Point2D, Point2D, Point2D],
): Float64Array | null {
  // Build the 8×9 matrix A for the DLT (Direct Linear Transform).
  // For each point pair (sx,sy) → (dx,dy):
  //   [-sx, -sy, -1,   0,   0,  0, sx*dx, sy*dx, dx]
  //   [  0,   0,  0, -sx, -sy, -1, sx*dy, sy*dy, dy]
  // We solve Ah = 0 with h9=1 → rearrange to Bh' = c (8×8 system).

  const A: number[][] = [];
  const b: number[] = [];

  for (let i = 0; i < 4; i++) {
    const sx = src[i].x, sy = src[i].y;
    const dx = dst[i].x, dy = dst[i].y;

    A.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy]);
    b.push(dx);
    A.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy]);
    b.push(dy);
  }

  // Solve 8×8 linear system via Gaussian elimination
  const h = solveLinear8(A, b);
  if (!h) return null;

  // H = [h0 h1 h2; h3 h4 h5; h6 h7 1]
  const H = new Float64Array(9);
  for (let i = 0; i < 8; i++) H[i] = h[i];
  H[8] = 1;
  return H;
}

/**
 * Apply homography H to a point (x,y). Returns the transformed (u,v).
 */
export function applyHomography(H: Float64Array, p: Point2D): Point2D {
  const w = H[6] * p.x + H[7] * p.y + H[8];
  if (Math.abs(w) < 1e-12) return { x: 0, y: 0 };
  return {
    x: (H[0] * p.x + H[1] * p.y + H[2]) / w,
    y: (H[3] * p.x + H[4] * p.y + H[5]) / w,
  };
}

/**
 * Classify a paper-coordinate point into a region.
 * Paper coordinates: (0,0) = top-left, (1,1) = bottom-right.
 */
export function classifyPaperRegion(u: number, v: number): PaperRegion {
  if (u < -0.05 || u > 1.05 || v < -0.05 || v > 1.05) return 'outside';

  const col = u < 0.33 ? 'left' : u < 0.67 ? 'center' : 'right';
  const row = v < 0.33 ? 'top' : v < 0.67 ? 'mid' : 'bottom';
  return `${row}_${col}` as PaperRegion;
}

/**
 * 3-zone classification: confident_inside / boundary / confident_outside.
 * `margin` defines the boundary width as a fraction of the paper dimension.
 */
export type PaperZone = 'confident_inside' | 'boundary' | 'confident_outside';

export const PAPER_ZONE_META: Record<PaperZone, { label: string; color: string }> = {
  confident_inside:  { label: '시험지 안',  color: '#22c55e' },
  boundary:          { label: '경계',       color: '#eab308' },
  confident_outside: { label: '시험지 밖',  color: '#ef4444' },
};

export function classifyPaperZone(u: number, v: number, margin = 0.15): PaperZone {
  if (u >= 0 && u <= 1 && v >= 0 && v <= 1) return 'confident_inside';
  if (u >= -margin && u <= 1 + margin && v >= -margin && v <= 1 + margin) return 'boundary';
  return 'confident_outside';
}

export interface RaySampleResult {
  /** Per-sample paper coords + zone along the ray. */
  samples: Array<{ u: number; v: number; zone: PaperZone }>;
  insideRatio: number;
  boundaryRatio: number;
  outsideRatio: number;
  /** Zone holding the most samples. */
  dominantZone: PaperZone;
}

/**
 * §5.10 — sample MULTIPLE points along the gaze ray rather than trusting a
 * single endpoint. Each image-space sample is mapped through H to paper-
 * normalized coords and classified inside/boundary/outside; the per-zone ratio
 * + dominant zone are returned. Robust to a single noisy projection.
 *
 * @param origin ray origin (image-normalized)
 * @param dir    ray direction (image space; any length)
 * @param H      image→paper homography
 */
export function sampleRayInPaper(
  origin: Point2D, dir: Point2D, H: Float64Array,
  opts: { samples?: number; tMin?: number; tMax?: number; margin?: number } = {},
): RaySampleResult {
  const n = Math.max(2, opts.samples ?? 12);
  const tMin = opts.tMin ?? 0;
  const tMax = opts.tMax ?? 1;
  const margin = opts.margin ?? 0.15;
  const samples: Array<{ u: number; v: number; zone: PaperZone }> = [];
  const counts: Record<PaperZone, number> = {
    confident_inside: 0, boundary: 0, confident_outside: 0,
  };
  for (let i = 0; i < n; i++) {
    const t = tMin + (tMax - tMin) * (i / (n - 1));
    const p = applyHomography(H, { x: origin.x + dir.x * t, y: origin.y + dir.y * t });
    const zone = classifyPaperZone(p.x, p.y, margin);
    samples.push({ u: p.x, v: p.y, zone });
    counts[zone] += 1;
  }
  let dominantZone: PaperZone = 'confident_inside';
  (Object.keys(counts) as PaperZone[]).forEach((z) => {
    if (counts[z] > counts[dominantZone]) dominantZone = z;
  });
  return {
    samples,
    insideRatio: counts.confident_inside / n,
    boundaryRatio: counts.boundary / n,
    outsideRatio: counts.confident_outside / n,
    dominantZone,
  };
}

/**
 * Intersection of two lines, each defined by two points.
 * Returns null if lines are (near-)parallel.
 */
export function lineIntersect(
  a1: Point2D, a2: Point2D, b1: Point2D, b2: Point2D,
): Point2D | null {
  const dxa = a1.x - a2.x, dya = a1.y - a2.y;
  const dxb = b1.x - b2.x, dyb = b1.y - b2.y;
  const denom = dxa * dyb - dya * dxb;
  if (Math.abs(denom) < 1e-9) return null;
  const ca = a1.x * a2.y - a1.y * a2.x;
  const cb = b1.x * b2.y - b1.y * b2.x;
  return {
    x: (ca * dxb - cb * dxa) / denom,
    y: (ca * dyb - cb * dya) / denom,
  };
}

/**
 * Given 8 edge points ordered [top1, top2, right1, right2, bottom1, bottom2, left1, left2],
 * compute the 4 paper corners via line intersections. Returns null if any pair of
 * adjacent edges is parallel/degenerate.
 * Output order matches PAPER_DST: [TL, TR, BR, BL].
 */
export function cornersFromEdgePoints(pts: Point2D[]): [Point2D, Point2D, Point2D, Point2D] | null {
  if (pts.length < 8) return null;
  const [t1, t2, r1, r2, b1, b2, l1, l2] = pts;
  const TL = lineIntersect(t1, t2, l1, l2);
  const TR = lineIntersect(t1, t2, r1, r2);
  const BR = lineIntersect(b1, b2, r1, r2);
  const BL = lineIntersect(b1, b2, l1, l2);
  if (!TL || !TR || !BR || !BL) return null;
  return [TL, TR, BR, BL];
}

/**
 * Standard paper destination corners (normalized unit rectangle).
 * Order: top-left, top-right, bottom-right, bottom-left.
 */
export const PAPER_DST: [Point2D, Point2D, Point2D, Point2D] = [
  { x: 0, y: 0 }, // top-left
  { x: 1, y: 0 }, // top-right
  { x: 1, y: 1 }, // bottom-right
  { x: 0, y: 1 }, // bottom-left
];

/**
 * Default on-screen paper quad (normalized TL/TR/BR/BL) used as the initial
 * outline so the paper overlay shows at a sensible desk position without
 * calibration. Shared by the live demo and the scene-log app so both draw
 * the same outline.
 */
export const PAPER_CORNERS_DEFAULT: Point2D[] = [
  { x: 0.22, y: 0.62 },
  { x: 0.78, y: 0.62 },
  { x: 0.92, y: 0.92 },
  { x: 0.08, y: 0.92 },
];

// ── Affine fallback (3-corner partial homography) ────────────
//
// 3꼭지: 시험지 4점 중 1점이 잘렸을 때 affine transform으로 fallback.
// Affine = perspective의 subset (last row [0 0 1]). 3 point pair → 6 DoF
// 정확히 풀이 가능. 가정: 카메라가 시험지와 거의 평행 → perspective
// 왜곡이 작아서 affine 근사가 충분히 좋음. 단 종횡비/원근감이 큰 경우
// 오차 발생 → 신뢰도가 낮음을 호출자가 명시해야 함.
export function computeAffine(
  src: [Point2D, Point2D, Point2D],
  dst: [Point2D, Point2D, Point2D],
): Float64Array | null {
  // [ sx sy 1 0  0  0 ] [a]   [dx]
  // [ 0  0  0 sx sy 1 ] [b] = [dy]
  //                     [c]
  //                     [d]
  //                     [e]
  //                     [f]
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 3; i++) {
    const sx = src[i].x, sy = src[i].y;
    const dx = dst[i].x, dy = dst[i].y;
    A.push([sx, sy, 1, 0, 0, 0]); b.push(dx);
    A.push([0, 0, 0, sx, sy, 1]); b.push(dy);
  }
  const h = solveLinearN(A, b, 6);
  if (!h) return null;
  const H = new Float64Array(9);
  H[0] = h[0]; H[1] = h[1]; H[2] = h[2];
  H[3] = h[3]; H[4] = h[4]; H[5] = h[5];
  H[6] = 0;    H[7] = 0;    H[8] = 1;
  return H;
}

export type PartialHomographyKind = 'full' | 'affine' | 'none';

export interface PartialHomographyResult {
  /** Resulting 3x3 matrix, or null if the configuration cannot be solved. */
  H: Float64Array | null;
  /** Which estimator produced H. 'full' = 4-point DLT, 'affine' = 3-point. */
  kind: PartialHomographyKind;
  /** Confidence proxy [0,1]. 1 = full 4-point, ~0.7 = 3-point affine, 0 = none. */
  confidence: number;
  /** Indices of source points used (subset of [0,1,2,3]). */
  usedIndices: number[];
}

/**
 * Compute a homography from a partial set of corners.
 *
 * @param src  4 source points (in PAPER_DST order: TL, TR, BR, BL).
 * @param dst  4 destination points (typically PAPER_DST).
 * @param visibleIdx  Indices into src/dst that are visible/trusted. When
 *                    length≥4 the standard 4-point DLT runs; length===3
 *                    falls back to affine; <3 returns null.
 */
export function computePartialHomography(
  src: Point2D[],
  dst: Point2D[],
  visibleIdx: number[],
): PartialHomographyResult {
  if (visibleIdx.length >= 4) {
    const s: [Point2D, Point2D, Point2D, Point2D] = [
      src[visibleIdx[0]], src[visibleIdx[1]], src[visibleIdx[2]], src[visibleIdx[3]],
    ];
    const d: [Point2D, Point2D, Point2D, Point2D] = [
      dst[visibleIdx[0]], dst[visibleIdx[1]], dst[visibleIdx[2]], dst[visibleIdx[3]],
    ];
    const H = computeHomography(s, d);
    return { H, kind: H ? 'full' : 'none', confidence: H ? 1 : 0, usedIndices: visibleIdx.slice(0, 4) };
  }
  if (visibleIdx.length === 3) {
    const s: [Point2D, Point2D, Point2D] = [
      src[visibleIdx[0]], src[visibleIdx[1]], src[visibleIdx[2]],
    ];
    const d: [Point2D, Point2D, Point2D] = [
      dst[visibleIdx[0]], dst[visibleIdx[1]], dst[visibleIdx[2]],
    ];
    const H = computeAffine(s, d);
    // Affine is exact only when source/dest are affinely related; for typical
    // exam-paper setups the actual perspective warp is small, so we report
    // ~0.7 confidence as a hint that gaze-zone classification near the
    // missing corner should be treated cautiously.
    return { H, kind: H ? 'affine' : 'none', confidence: H ? 0.7 : 0, usedIndices: visibleIdx.slice(0, 3) };
  }
  return { H: null, kind: 'none', confidence: 0, usedIndices: visibleIdx.slice(0) };
}

// ── 8×8 Gaussian elimination ─────────────────────────────────

function solveLinearN(A: number[][], b: number[], n: number): number[] | null {
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let maxRow = col;
    let maxVal = Math.abs(M[col][col]);
    for (let row = col + 1; row < n; row++) {
      const v = Math.abs(M[row][col]);
      if (v > maxVal) { maxVal = v; maxRow = row; }
    }
    if (maxVal < 1e-12) return null;
    if (maxRow !== col) { const tmp = M[col]; M[col] = M[maxRow]; M[maxRow] = tmp; }
    const pivot = M[col][col];
    for (let row = col + 1; row < n; row++) {
      const factor = M[row][col] / pivot;
      for (let j = col; j <= n; j++) M[row][j] -= factor * M[col][j];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let sum = M[i][n];
    for (let j = i + 1; j < n; j++) sum -= M[i][j] * x[j];
    x[i] = sum / M[i][i];
  }
  return x;
}

function solveLinear8(A: number[][], b: number[]): number[] | null {
  const n = 8;
  // Augmented matrix [A | b]
  const M = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    // Partial pivoting
    let maxRow = col;
    let maxVal = Math.abs(M[col][col]);
    for (let row = col + 1; row < n; row++) {
      const v = Math.abs(M[row][col]);
      if (v > maxVal) { maxVal = v; maxRow = row; }
    }
    if (maxVal < 1e-12) return null; // singular
    if (maxRow !== col) { const tmp = M[col]; M[col] = M[maxRow]; M[maxRow] = tmp; }

    // Eliminate below
    const pivot = M[col][col];
    for (let row = col + 1; row < n; row++) {
      const factor = M[row][col] / pivot;
      for (let j = col; j <= n; j++) M[row][j] -= factor * M[col][j];
    }
  }

  // Back-substitution
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let sum = M[i][n];
    for (let j = i + 1; j < n; j++) sum -= M[i][j] * x[j];
    x[i] = sum / M[i][i];
  }
  return x;
}
